import { ContentGroup } from 'src/gtm';

/**
 * Derives the GTM `content_group` purely from the URL.
 *
 * IMPORTANT: this function must stay fully self-contained — no imports or
 * closure references in the body — because it is serialized via `.toString()`
 * into an inline <script> in `_document.tsx` so the value can be pushed to
 * `dataLayer` BEFORE the GTM container initializes on a full page load. The
 * same function is also called from `_app.tsx` on client-side navigation.
 */
export function getContentGroupFromPath(
  pathname: string,
  search: string
): ContentGroup {
  var path = (pathname || '').toLowerCase().replace(/\/+$/, '');
  var qs = (search || '').toLowerCase();

  if (path === '') return 'main';

  // Blog
  if (path === '/blog' || path.indexOf('/blog/') === 0) return 'blog';

  // Account (logged-in areas)
  if (
    path === '/profile' ||
    path === '/myfavs' ||
    path === '/myfavs/buildings' ||
    path === '/saved-searches' ||
    path === '/loginasuser'
  ) {
    return 'account';
  }

  // Static informational pages
  if (
    path === '/about' ||
    path === '/privacy-policy' ||
    path === '/terms-of-use' ||
    path === '/rent-vs-buy-calculator' ||
    path === '/sitemap' ||
    path === '/unsubscribe' ||
    path === '/404'
  ) {
    return 'other';
  }

  // Search page — rent vs sale decided by the forRent query param
  if (path === '/search') {
    return qs.indexOf('forrent=true') !== -1 ? 'rent' : 'sales';
  }

  // Unit pages encode rent/sale in the slug (e.g. .../unit-1601-for-rent)
  if (path.indexOf('for-rent') !== -1) return 'rent';
  if (path.indexOf('for-sale') !== -1) return 'sales';

  // Dynamic real-estate routes by segment depth:
  // 1 segment => neighborhood, 2 => building, 3+ => unit (default to sales)
  var segments = path.split('/');
  var count = 0;
  for (var i = 0; i < segments.length; i++) {
    if (segments[i]) count++;
  }
  if (count === 1) return 'neighborhoods';
  if (count === 2) return 'buildings';
  if (count >= 3) return 'sales';

  return 'other';
}

/**
 * Resolves the content group for a (possibly relative) URL string — used on
 * client-side navigation where Next's router gives us an asPath that may
 * include a query string and/or a hash fragment (e.g. "/search#map").
 *
 * We must NOT naively split on "?" because a fragment without a query would
 * stay attached to the pathname and break the mapping. `URL` parsing puts the
 * hash into `.hash` and keeps `.pathname`/`.search` clean.
 */
export function resolveContentGroupFromUrl(
  url: string,
  origin: string
): ContentGroup {
  const parsed = new URL(url, origin);
  return getContentGroupFromPath(parsed.pathname, parsed.search);
}
